import type { GetServerSideProps, NextPage } from 'next';
import { Layout } from '@stitch-fix/sf-next';
import { useScreenViewEvent } from '@stitch-fix/event-reporter';
import Head from 'next/head';
import { useState } from 'react';
import { ResponsiveSection } from '@stitch-fix/mode-react';
import { GetClientCapabilitiesAndBusinessLineQuery, useGetClientCapabilitiesAndBusinessLineQuery } from '../../../utils/hooks/getClientCapabilitiesAndBusinessLine';
import { ToastProvider } from '../../../utils/contexts/toast-provider';
import BoughtItemsLayout from '../../../components/BoughtItemsLayout/BoughtItemsLayout';
import { hasPermissions } from '../permissions';
import { BoughtItemsStateMachineProvider } from '../../../utils/machines/boughtItems/context-provider';
import { ClientContextProvider } from '../../../utils/contexts/client-context-provider';
import { useRedirect } from '../../../utils/hooks/useRedirect';
import { PagePlaceholder } from '../../../components/PagePlaceholder/PagePlaceholder';
import { getWardrobeCopy } from '../../../utils/getWardrobeCopy';
import { WardrobeCopyProvider } from '../../../utils/contexts/copy-provider';
import { WardrobeCopyFragment } from '../../../utils/hooks/getWardrobeCopy';
export type WardobePageProps = {
  copy: WardrobeCopyFragment;
};
export const getServerSideProps: GetServerSideProps = async ({
  req,
  locale
}) => {
  const copy = await getWardrobeCopy(req);

  // freestyle isn't available in the UK
  if (locale === 'en-GB') {
    return {
      notFound: true
    };
  }
  return {
    props: {
      copy
    }
  };
};

/**
 * The `BoughtItemsPage` component redirects to `/home`
 * if client does not satisfy the permission requirements. In order for
 * the client to access the bought items page they need to be able to shop.
 */
const BoughtItemsPage: NextPage<WardobePageProps> = ({
  copy
}) => {
  const [isMissingPermissions, setIsMissingPermissions] = useState(false);
  const {
    data: clientInfoData
  } = useGetClientCapabilitiesAndBusinessLineQuery({
    onCompleted: (d: GetClientCapabilitiesAndBusinessLineQuery) => {
      setIsMissingPermissions(!hasPermissions(d));
    }
  });
  const canRenderPageContent = hasPermissions(clientInfoData);
  useRedirect({
    shouldRedirect: isMissingPermissions,
    path: '/home'
  });

  // v2 page view event
  const {
    EventProvider
  } = useScreenViewEvent({
    schema: 'screen_view',
    name: 'wardrobe_bought_items',
    source_app: 'categories-next-ui'
  });
  return <Layout responsiveSection={{
    width: 'full-bleed',
    gutter: 'none',
    mt: 0
  }}>
      <Head>
        <title>Your Wardrobe | Bought Items</title>
        <meta name="description" content="Bought Items" />
      </Head>
      <WardrobeCopyProvider copy={copy}>
        {canRenderPageContent ? <EventProvider>
            <ClientContextProvider clientBusinessLine={clientInfoData?.client?.businessLine}>
              <BoughtItemsStateMachineProvider>
                <ToastProvider>
                  <BoughtItemsLayout />
                </ToastProvider>
              </BoughtItemsStateMachineProvider>
            </ClientContextProvider>
          </EventProvider> : <ResponsiveSection width="xlarge" mt={2}>
            <PagePlaceholder />
          </ResponsiveSection>}
      </WardrobeCopyProvider>
    </Layout>;
};
export default BoughtItemsPage;